import { DEFAULT_VARIABLE, type VariableKey } from './census-variables';
import type { Lang } from './i18n';
import { buildShareUrl } from './urlState';

export type ShareChannel =
  | 'native'
  | 'whatsapp'
  | 'copy'
  | 'instagram'
  | 'facebook'
  | 'x'
  | 'email';

export interface ShareInput {
  geoid: string;
  municipio: string;
  variable: VariableKey;
  formattedValue: string;
  lang: Lang;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

export type ShareResult = 'ok' | 'copied';

function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function buildShareMessage(input: ShareInput): string {
  const { variable, municipio, formattedValue, t } = input;
  const key = `share.${variable}`;
  const msg = t(key, { municipio, value: formattedValue });
  if (msg === key) return t('share.default', { municipio });
  return msg;
}

export function buildOgImageUrl(input: ShareInput, format: 'card' | 'story'): string {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://mapa.revistaetnica.com';
  const p = new URLSearchParams({
    geoid: input.geoid,
    v: input.variable,
    lang: input.lang,
  });
  if (format === 'story') p.set('format', 'story');
  return `${origin}/og/card.png?${p.toString()}`;
}

function openPopup(url: string) {
  // 720×640 opens a desktop share dialog without trashing the current tab,
  // and on mobile browsers this falls back to a normal new-tab navigation.
  const w = 720;
  const h = 640;
  const left = typeof window !== 'undefined' ? Math.max(0, (window.innerWidth - w) / 2) : 0;
  const top = typeof window !== 'undefined' ? Math.max(0, (window.innerHeight - h) / 2) : 0;
  const features = `popup=yes,noopener,noreferrer,width=${w},height=${h},left=${left},top=${top}`;
  window.open(url, '_blank', features);
}

export async function openShare(
  channel: ShareChannel,
  input: ShareInput,
): Promise<ShareResult> {
  const url = buildShareUrl(input.geoid, input.variable, input.lang);
  const text = buildShareMessage(input);

  switch (channel) {
    case 'native': {
      const nav = globalThis.navigator as Navigator & {
        share?: (d: ShareData) => Promise<void>;
        canShare?: (d: ShareData) => boolean;
      };
      if (typeof nav.share === 'function') {
        // Plain-text share — the OS share sheet handles routing to whatever
        // the user picks (AirDrop, Messages, WhatsApp, etc.). Include both
        // text and url so receivers that render only one still read well.
        await nav.share({ title: input.municipio, text, url });
        return 'ok';
      }
      await nav.clipboard.writeText(`${text} ${url}`);
      return 'copied';
    }

    case 'copy': {
      // Copies "<prefilled sentence> <deep link>" rather than just the URL —
      // what most users actually want to paste into a message is the full
      // sentence, not a bare link that then needs a human sentence written
      // around it.
      await navigator.clipboard.writeText(`${text} ${url}`);
      return 'copied';
    }

    case 'whatsapp': {
      // wa.me on mobile picks the WhatsApp app automatically; on desktop
      // web.whatsapp.com has better behavior than wa.me (which nags a
      // Continue to Chat gate). Both prefill "<text> <url>".
      const base = isMobile() ? 'https://wa.me/?text=' : 'https://web.whatsapp.com/send?text=';
      const full = encodeURIComponent(`${text} ${url}`);
      const target = `${base}${full}`;
      if (isMobile()) {
        window.location.href = target;
      } else {
        openPopup(target);
      }
      return 'ok';
    }

    case 'facebook': {
      // FB scrapes OG meta from the shared URL to build its card — the quote
      // param lets us also prefill the text inside the post composer.
      const fb =
        'https://www.facebook.com/sharer/sharer.php?u=' +
        encodeURIComponent(url) +
        '&quote=' +
        encodeURIComponent(text);
      openPopup(fb);
      return 'ok';
    }

    case 'x': {
      const x =
        'https://twitter.com/intent/tweet?text=' +
        encodeURIComponent(text) +
        '&url=' +
        encodeURIComponent(url);
      openPopup(x);
      return 'ok';
    }

    case 'email': {
      // Subject is just the municipio name (short, scannable in an inbox);
      // body is the prefilled sentence + URL on its own line so email
      // clients auto-link it.
      const subject = encodeURIComponent(`${input.municipio} · Mapa Afro-Demográfico`);
      const body = encodeURIComponent(`${text}\n\n${url}`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
      return 'ok';
    }

    case 'instagram': {
      // IG has no web intent — the only reliable path is to get the 1080×1920
      // image into the user's share sheet as a File, where they pick IG and
      // the Stories editor loads it. Desktop fallback: clean download.
      const imgUrl = buildOgImageUrl(input, 'story');
      const res = await fetch(imgUrl);
      if (!res.ok) throw new Error(`story image ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], `${input.municipio}-etnica.png`, { type: 'image/png' });

      const nav = globalThis.navigator as Navigator & {
        share?: (d: ShareData) => Promise<void>;
        canShare?: (d: ShareData) => boolean;
      };
      if (nav.canShare?.({ files: [file] }) && nav.share) {
        await nav.share({ files: [file], title: input.municipio, text });
        return 'ok';
      }

      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      }, 1000);
      return 'ok';
    }
  }
}

export function trackShare(input: ShareInput, channel: ShareChannel) {
  const payload = {
    municipio_geoid: input.geoid,
    municipio_name: input.municipio,
    variable: input.variable,
    channel,
    lang: input.lang,
  };
  window.dispatchEvent(new CustomEvent('share_click', { detail: payload }));
}

export function defaultVarForShare(v: VariableKey): VariableKey {
  return v ?? DEFAULT_VARIABLE;
}
